import * as Popover from '@radix-ui/react-popover';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Calendar,
  ChevronDown,
  Pencil,
  RefreshCw,
  Reply,
  Smartphone,
  Sparkles,
  SlidersHorizontal,
  Star,
  ThumbsUp,
  Trash2,
  X,
} from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import StarSeekerIcon from '../../assets/svg/star-seeker.svg?react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { usePublisher } from '../../hooks/usePublisher';
import { cn } from '../../lib/utils';
import { Button } from '../Button';
import { useOptionalDappContext } from '../DappLayout';
import {
  EASE_OUT,
  InitialsAvatar,
  listItem,
  PortalCard,
  PortalPage,
  PortalPageHeader,
  Skeleton,
  SkeletonGroup,
  SkeletonText,
  StarRating,
} from '../portal';
import {
  CharacterCountHint,
  charactersLeft,
} from '../shared/CharacterCountHint';
import {
  formatDigestWindow,
  getReviewerLabel,
  truncateMiddle,
} from './dashboardReviewFormatting';
import {
  type RatingFilter,
  type ReviewFilters,
  type ReviewRow,
  type ReviewsPageData,
  type SortOption,
  type TimeFilter,
  type WeeklyDigest,
  useDashboardReviewsData,
} from './useDashboardReviewsData';

const TIME_FILTER_LABEL: Record<TimeFilter, string> = {
  day: '1 day',
  week: '7 days',
  month: '30 days',
};

const SORT_LABEL: Record<SortOption, string> = {
  newest: 'Newest',
  oldest: 'Oldest',
  highest: 'Highest rated',
  lowest: 'Lowest rated',
};

const REPLY_MAX_LENGTH = 500;

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70';

export const DashboardReviews = () => {
  const dappId = useOptionalDappContext()?.dappId;
  const {
    canRender,
    isChecking,
    dappName,
    data,
    displayData,
    error,
    isLoading,
    digest,
    isDigestLoading,
    latestVersionPercent,
    reviewsByRating,
    totalRatings,
    totalPages,
    currentPage,
    isInitialLoad,
    hasError,
    showDigestPanel,
    storeListingMissing,
    filters,
    setFilters,
    sortBy,
    setSortBy,
    activeFilterCount,
    mutatingReviewId,
    handleNextPage,
    handlePreviousPage,
    handleRefresh,
    handleSaveReply,
    handleDeleteReply,
  } = useDashboardReviewsData({ dappId });

  usePageTitle(
    dappId && dappName
      ? `${dappName} - Ratings & Reviews | Publishing Portal`
      : 'Ratings & Reviews | Publishing Portal',
  );

  if (isChecking || !canRender) {
    return null;
  }

  return (
    <PortalPage contentClassName="gap-6">
      <PortalPageHeader
        icon={<Star />}
        title="Ratings & Reviews"
        className="mb-4"
        actions={
          dappId ? (
            <Button
              variant="ghostMuted"
              onClick={handleRefresh}
              disabled={isLoading}
              aria-label="Refresh ratings and reviews"
              title="Refresh"
              className="size-10 px-0"
            >
              <RefreshCw
                className={cn('size-4', isLoading && 'animate-spin')}
              />
            </Button>
          ) : undefined
        }
      />

      {!dappId ? (
        <EmptyState title="No dApp selected" />
      ) : (
        <>
          <RatingSummaryCard
            data={data}
            isInitialLoad={isInitialLoad}
            hasError={hasError}
            storeListingMissing={storeListingMissing}
            totalRatings={totalRatings}
            reviewsByRating={reviewsByRating}
            latestVersionPercent={latestVersionPercent}
          />

          <div
            className={cn(
              'grid items-start gap-6',
              showDigestPanel
                ? 'lg:grid-cols-[minmax(0,30rem)_minmax(0,1fr)]'
                : 'lg:grid-cols-1',
            )}
          >
            {showDigestPanel && (
              <WeeklyDigestPanel digest={digest} isLoading={isDigestLoading} />
            )}

            <UserReviewsPanel
              data={data}
              displayData={displayData}
              error={error}
              isLoading={isLoading}
              currentPage={currentPage}
              totalPages={totalPages}
              onNext={handleNextPage}
              onPrevious={handlePreviousPage}
              onRetry={handleRefresh}
              isInitialLoad={isInitialLoad}
              hasError={hasError}
              filters={filters}
              onFiltersChange={setFilters}
              sortBy={sortBy}
              onSortChange={setSortBy}
              activeFilterCount={activeFilterCount}
              mutatingReviewId={mutatingReviewId}
              onSaveReply={handleSaveReply}
              onDeleteReply={handleDeleteReply}
            />
          </div>
        </>
      )}
    </PortalPage>
  );
};

function RatingSummaryCard({
  data,
  isInitialLoad,
  hasError,
  storeListingMissing,
  totalRatings,
  reviewsByRating,
  latestVersionPercent,
}: {
  data: ReviewsPageData | null;
  isInitialLoad: boolean;
  hasError: boolean;
  storeListingMissing: boolean;
  totalRatings: number;
  reviewsByRating: number[];
  latestVersionPercent: number | null;
}) {
  const firstReleaseLabel = data?.firstReleaseDate
    ? formatFullDate(data.firstReleaseDate)
    : null;
  const metricsUnavailable = hasError || storeListingMissing;
  const replyCount = data?.summary.replyCount ?? null;

  return (
    <PortalCard className="flex flex-col gap-6 p-5">
      {isInitialLoad ? (
        <SkeletonGroup
          label="Loading release date"
          className="flex h-4 items-center"
        >
          <Skeleton className="h-3 w-56" />
        </SkeletonGroup>
      ) : (
        <div className="text-xs leading-4 text-seeker-400">
          {firstReleaseLabel
            ? `Since First Release (${firstReleaseLabel})`
            : 'Rating Analytics'}
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,60%)] xl:items-center">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:gap-4">
          {isInitialLoad ? (
            <RatingScoreSkeleton />
          ) : (
            <RatingScore
              rating={data?.summary.rating ?? null}
              isUnavailable={metricsUnavailable}
            />
          )}
          {isInitialLoad ? (
            <RatingDistributionSkeleton />
          ) : (
            <RatingDistribution
              buckets={reviewsByRating}
              total={totalRatings}
              isUnavailable={metricsUnavailable}
            />
          )}
        </div>

        <div className="grid gap-2 sm:grid-cols-3">
          <MetricTile
            icon={<Star />}
            label={totalRatings === 1 ? 'Total Review' : 'Total Reviews'}
            value={metricsUnavailable ? '—' : totalRatings.toLocaleString()}
            isLoading={isInitialLoad}
          />
          <MetricTile
            icon={<Reply />}
            label={replyCount === 1 ? 'Reply' : 'Replies'}
            value={
              metricsUnavailable || replyCount === null
                ? '—'
                : replyCount.toLocaleString()
            }
            isLoading={isInitialLoad}
          />
          <MetricTile
            icon={<Smartphone />}
            label="On latest version"
            value={
              metricsUnavailable || latestVersionPercent === null
                ? '—'
                : `${latestVersionPercent.toFixed(1)}%`
            }
            isLoading={isInitialLoad}
          />
        </div>
      </div>
    </PortalCard>
  );
}

function WeeklyDigestPanel({
  digest,
  isLoading,
}: {
  digest: WeeklyDigest | null;
  isLoading: boolean;
}) {
  const isSkeleton = isLoading && !digest;

  return (
    <PortalCard className="p-6">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-medium text-white">
            <Calendar className="size-4" />
            Weekly Digest
          </div>
          {isSkeleton ? (
            <SkeletonGroup
              label="Loading digest window"
              className="mt-1 flex h-4 items-center"
            >
              <Skeleton className="h-3 w-44" />
            </SkeletonGroup>
          ) : digest ? (
            <div className="mt-1 text-xs leading-4 text-seeker-400">
              {formatDigestWindow(digest.windowStart, digest.windowEnd)}
            </div>
          ) : null}
        </div>
        <span className="inline-flex shrink-0 items-center gap-1 text-xs text-seeker-500">
          <Sparkles className="size-3.5" />
          AI summary
        </span>
      </header>

      <div className="mt-6">
        {isSkeleton ? (
          <SkeletonGroup
            label="Loading weekly digest"
            className="flex flex-col"
          >
            {['w-full', 'w-[94%]', 'w-[88%]', 'w-[72%]'].map((width) => (
              <div key={width} className="flex h-6 items-center">
                <Skeleton className={cn('h-3.5', width)} />
              </div>
            ))}
          </SkeletonGroup>
        ) : digest?.summary ? (
          <p className="motion-enter whitespace-pre-line text-base leading-6 text-white">
            {digest.summary}
          </p>
        ) : null}
      </div>

      <div className="mt-6">
        <div className="text-xs leading-4 text-seeker-400">
          Reviews This Week
        </div>
        <div className="mt-3 inline-flex h-10 items-center gap-2 rounded-lg border border-seeker-700 px-4 text-sm text-white">
          <ThumbsUp className="size-4 text-seeker-400" />
          {isSkeleton ? (
            <Skeleton className="h-3 w-20" />
          ) : (
            <span className="tabular-nums">
              {(digest?.inputReviewCount ?? 0).toLocaleString()}{' '}
              {digest?.inputReviewCount === 1 ? 'review' : 'reviews'}
            </span>
          )}
        </div>
      </div>
    </PortalCard>
  );
}

function UserReviewsPanel({
  data,
  displayData,
  error,
  isLoading,
  currentPage,
  totalPages,
  onNext,
  onPrevious,
  onRetry,
  isInitialLoad,
  hasError,
  filters,
  onFiltersChange,
  sortBy,
  onSortChange,
  activeFilterCount,
  mutatingReviewId,
  onSaveReply,
  onDeleteReply,
}: {
  data: ReviewsPageData | null;
  displayData: ReviewsPageData | null;
  error: string | null;
  isLoading: boolean;
  currentPage: number;
  totalPages: number;
  onNext: () => void;
  onPrevious: () => void;
  onRetry: () => void;
  isInitialLoad: boolean;
  hasError: boolean;
  filters: ReviewFilters;
  onFiltersChange: (next: ReviewFilters) => void;
  sortBy: SortOption;
  onSortChange: (next: SortOption) => void;
  activeFilterCount: number;
  mutatingReviewId: string | null;
  onSaveReply: (review: ReviewRow, reply: string) => Promise<boolean>;
  onDeleteReply: (review: ReviewRow) => Promise<void>;
}) {
  const storeListingMissing = data !== null && !data.storeListingFound;

  const clearRating = () => onFiltersChange({ ...filters, minRating: null });
  const clearTime = () => onFiltersChange({ ...filters, timeWindow: null });

  return (
    <PortalCard className="flex flex-col p-6">
      <header className="flex items-center gap-2 text-sm font-medium text-white">
        <Star className="size-4" />
        User Reviews
      </header>

      {!storeListingMissing && (
        <div className="mt-6 flex flex-wrap items-center gap-2 border-b border-seeker-800 pb-6 text-sm">
          <FilterPopover
            filters={filters}
            onChange={onFiltersChange}
            activeCount={activeFilterCount}
          />

          {filters.minRating !== null && (
            <FilterChip
              ariaLabel={`Remove rating filter ${filters.minRating} and up`}
              onRemove={clearRating}
            >
              <StarRating
                rating={filters.minRating}
                size={10}
                label={`${filters.minRating} stars`}
              />
              <span>&amp; Up</span>
            </FilterChip>
          )}
          {filters.timeWindow !== null && (
            <FilterChip
              ariaLabel={`Remove time filter ${TIME_FILTER_LABEL[filters.timeWindow]}`}
              onRemove={clearTime}
            >
              <span>{TIME_FILTER_LABEL[filters.timeWindow]}</span>
            </FilterChip>
          )}

          <div className="ml-auto flex items-center gap-3">
            <SortDropdown value={sortBy} onChange={onSortChange} />
            <div className="flex h-4 items-center text-xs leading-4 text-seeker-500">
              {isInitialLoad ? (
                <SkeletonGroup
                  label="Loading page indicator"
                  className="flex h-4 items-center"
                >
                  <Skeleton className="h-3 w-24" />
                </SkeletonGroup>
              ) : hasError ? (
                '—'
              ) : (
                <span className="tabular-nums">
                  Page {currentPage} of {totalPages}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={cn('flex-1', storeListingMissing && 'mt-6')}>
        <ReviewList
          data={displayData}
          error={error}
          isLoading={isLoading}
          onRetry={onRetry}
          hasActiveFilters={activeFilterCount > 0}
          unfilteredCount={data?.reviews.length ?? 0}
          mutatingReviewId={mutatingReviewId}
          onSaveReply={onSaveReply}
          onDeleteReply={onDeleteReply}
        />
      </div>

      {!storeListingMissing && (
        <div className="mt-6 flex items-center justify-between border-t border-seeker-800 pt-6">
          <Button
            variant="outlineMuted"
            onClick={onPrevious}
            disabled={currentPage === 1 || isLoading}
            className="h-9"
          >
            Previous
          </Button>
          <Button
            variant="primary"
            onClick={onNext}
            disabled={
              isLoading ||
              !data?.pageInfo.hasNextPage ||
              currentPage >= totalPages
            }
            className="h-9"
          >
            Next
          </Button>
        </div>
      )}
    </PortalCard>
  );
}

const POPOVER_CLASS =
  'z-50 origin-[var(--radix-popover-content-transform-origin)] rounded-lg bg-seeker-800 p-2 text-sm text-white shadow-[0_0_20px_rgba(0,0,0,0.25)] duration-150 ease-out data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.97] data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-[0.98]';

function FilterPopover({
  filters,
  onChange,
  activeCount,
}: {
  filters: ReviewFilters;
  onChange: (next: ReviewFilters) => void;
  activeCount: number;
}) {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex h-10 items-center gap-3 rounded-lg border border-seeker-700 px-4 text-white transition-colors duration-150 hover:bg-white/5 data-[state=open]:bg-white/5',
            FOCUS_RING,
          )}
        >
          <span className="text-sm font-bold">Filter</span>
          {activeCount > 0 && (
            <span className="inline-flex size-5 items-center justify-center rounded-full bg-white text-xs font-bold tabular-nums text-black">
              {activeCount}
            </span>
          )}
          <SlidersHorizontal className="size-4" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={8}
          className={cn(POPOVER_CLASS, 'w-72 p-4')}
        >
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-seeker-500">
              Rating
            </div>
            <div className="grid grid-cols-1 gap-1">
              {([5, 4, 3, 2, 1] as RatingFilter[]).map((value) => {
                const checked = filters.minRating === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() =>
                      onChange({
                        ...filters,
                        minRating: checked ? null : value,
                      })
                    }
                    className={cn(
                      'flex items-center justify-between rounded-lg px-2 py-1.5 text-left transition-colors duration-150',
                      FOCUS_RING,
                      checked ? 'bg-seeker-700 text-white' : 'hover:bg-white/5',
                    )}
                  >
                    <span className="flex items-center gap-2 text-sm">
                      <StarRating
                        rating={value}
                        size={10}
                        label={`${value} stars`}
                      />
                      &amp; Up
                    </span>
                    {checked && (
                      <span className="text-xs uppercase tracking-wider text-seeker-400">
                        Active
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-4">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-seeker-500">
              Time period
            </div>
            <div className="grid grid-cols-3 gap-1">
              {(Object.keys(TIME_FILTER_LABEL) as TimeFilter[]).map((value) => {
                const checked = filters.timeWindow === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() =>
                      onChange({
                        ...filters,
                        timeWindow: checked ? null : value,
                      })
                    }
                    className={cn(
                      'rounded-lg px-2 py-1.5 transition-colors duration-150',
                      FOCUS_RING,
                      checked
                        ? 'bg-seeker-700 text-white'
                        : 'text-seeker-300 hover:bg-white/5',
                    )}
                  >
                    <span className="text-sm">{TIME_FILTER_LABEL[value]}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {(filters.minRating !== null || filters.timeWindow !== null) && (
            <button
              type="button"
              onClick={() => onChange({ minRating: null, timeWindow: null })}
              className={cn(
                'mt-4 w-full rounded-lg border border-seeker-700 py-1.5 text-seeker-400 transition-colors duration-150 hover:bg-white/5 hover:text-white',
                FOCUS_RING,
              )}
            >
              <span className="text-sm">Clear all filters</span>
            </button>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function FilterChip({
  children,
  onRemove,
  ariaLabel,
}: {
  children: ReactNode;
  onRemove: () => void;
  ariaLabel: string;
}) {
  return (
    <span className="inline-flex h-10 items-center gap-2 rounded-lg border border-seeker-700 px-4 text-sm text-white">
      {children}
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={onRemove}
        className={cn(
          '-mr-1.5 flex size-5 items-center justify-center rounded text-seeker-400 transition-colors duration-150 hover:bg-white/5 hover:text-white',
          FOCUS_RING,
        )}
      >
        <X className="size-3" />
      </button>
    </span>
  );
}

function SortDropdown({
  value,
  onChange,
}: {
  value: SortOption;
  onChange: (next: SortOption) => void;
}) {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={cn(
            'group inline-flex h-10 items-center gap-1.5 rounded-lg px-2 text-white transition-colors duration-150 hover:bg-white/5 data-[state=open]:bg-white/5',
            FOCUS_RING,
          )}
        >
          <span className="text-sm">{SORT_LABEL[value]}</span>
          <ChevronDown className="size-4 transition-transform duration-150 group-data-[state=open]:rotate-180" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          className={cn(POPOVER_CLASS, 'w-44')}
        >
          {(Object.keys(SORT_LABEL) as SortOption[]).map((option) => {
            const isSelected = option === value;
            return (
              <Popover.Close
                key={option}
                asChild
                onClick={() => onChange(option)}
              >
                <button
                  type="button"
                  className={cn(
                    'flex w-full items-center rounded-lg px-2 py-1.5 text-left transition-colors duration-150',
                    FOCUS_RING,
                    isSelected
                      ? 'bg-seeker-700 text-white'
                      : 'hover:bg-white/5',
                  )}
                >
                  <span className="text-sm">{SORT_LABEL[option]}</span>
                </button>
              </Popover.Close>
            );
          })}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** Figma summary tile: 24px icon, 20px bold value, 12px label. */
function MetricTile({
  icon,
  label,
  value,
  isLoading,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  isLoading?: boolean;
}) {
  if (isLoading) {
    return (
      <SkeletonGroup
        label={`Loading ${label}`}
        className="flex min-h-[120px] flex-col justify-between gap-4 rounded-lg bg-seeker-800 px-6 py-5"
      >
        <Skeleton className="size-6 bg-seeker-700" />
        <div className="flex h-6 items-center">
          <Skeleton className="h-5 w-20 bg-seeker-700" />
        </div>
        <div className="flex h-4 items-center">
          <Skeleton className="h-3 w-24 bg-seeker-700" />
        </div>
      </SkeletonGroup>
    );
  }

  return (
    <div className="flex min-h-[120px] flex-col justify-between gap-4 rounded-lg bg-seeker-800 px-6 py-5 text-white [&>svg]:size-6 [&>svg]:shrink-0">
      {icon}
      <div className="motion-pop text-xl font-bold leading-6 tabular-nums">
        {value}
      </div>
      <div className="text-xs leading-4 text-seeker-400">{label}</div>
    </div>
  );
}

function RatingScore({
  rating,
  isUnavailable,
}: {
  rating: number | null;
  isUnavailable: boolean;
}) {
  return (
    <div className="motion-pop flex flex-col items-end">
      <div className="flex h-[72px] items-center gap-2">
        <StarSeekerIcon
          aria-hidden="true"
          className="size-9 shrink-0 fill-current text-white"
        />
        <div className="text-7xl font-medium leading-none tracking-tight text-white tabular-nums">
          {isUnavailable ? '—' : formatRating(rating)}
        </div>
      </div>
      <div className="text-sm leading-5 text-seeker-400">out of 5</div>
    </div>
  );
}

/** Mirrors `RatingScore`: 72px hero row (36px star + digits) and the 20px "out of 5" line. */
function RatingScoreSkeleton() {
  return (
    <SkeletonGroup
      label="Loading average rating"
      className="flex flex-col items-end"
    >
      <div className="flex h-[72px] items-center gap-2">
        <Skeleton className="size-9 shrink-0" />
        <Skeleton className="h-14 w-32" rounded="lg" />
      </div>
      <div className="flex h-5 items-center">
        <Skeleton className="h-3 w-14" />
      </div>
    </SkeletonGroup>
  );
}

function RatingDistribution({
  buckets,
  total,
  isUnavailable,
}: {
  buckets: number[];
  total: number;
  isUnavailable?: boolean;
}) {
  const rows = [5, 4, 3, 2, 1].map((stars) => ({
    stars,
    count: buckets[stars - 1] ?? 0,
  }));

  return (
    <div className="motion-pop flex flex-col justify-center gap-1">
      {rows.map((row) => {
        const pct = total === 0 ? 0 : (row.count / total) * 100;
        return (
          <div
            key={row.stars}
            className="grid h-4 grid-cols-[auto_2rem_minmax(0,188px)] items-center gap-2"
          >
            <StarRating
              rating={row.stars}
              size={10}
              label={`${row.stars} stars`}
            />
            <span className="text-xs leading-4 tabular-nums text-seeker-400">
              {isUnavailable ? '—' : `${Math.round(pct)}%`}
            </span>
            <div className="h-2 overflow-hidden rounded-sm bg-seeker-700">
              {!isUnavailable && (
                <div
                  className="h-full rounded-sm bg-white transition-[width] duration-200 ease-out"
                  style={{ width: `${pct}%` }}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Mirrors `RatingDistribution`: five 16px rows of stars / percent / 8px bar. */
function RatingDistributionSkeleton() {
  return (
    <SkeletonGroup
      label="Loading rating distribution"
      className="flex flex-col justify-center gap-1"
    >
      {[5, 4, 3, 2, 1].map((stars) => (
        <div
          key={stars}
          className="grid h-4 grid-cols-[auto_2rem_minmax(0,188px)] items-center gap-2"
        >
          <Skeleton className="h-2.5 w-14 rounded-sm" />
          <Skeleton className="h-3 w-6" />
          <Skeleton className="h-2 w-full rounded-sm" />
        </div>
      ))}
    </SkeletonGroup>
  );
}

function ReviewList({
  data,
  error,
  isLoading,
  onRetry,
  hasActiveFilters,
  unfilteredCount,
  mutatingReviewId,
  onSaveReply,
  onDeleteReply,
}: {
  data: ReviewsPageData | null;
  error: string | null;
  isLoading: boolean;
  onRetry: () => void;
  hasActiveFilters: boolean;
  unfilteredCount: number;
  mutatingReviewId: string | null;
  onSaveReply: (review: ReviewRow, reply: string) => Promise<boolean>;
  onDeleteReply: (review: ReviewRow) => Promise<void>;
}) {
  if (isLoading && !data) {
    return <ReviewListSkeleton />;
  }

  if (error) {
    return (
      <div className="rounded-lg border border-rose-500/40 bg-rose-900/20 p-5">
        <div className="text-sm font-medium text-rose-400">{error}</div>
        <Button variant="outlineMuted" onClick={onRetry} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  if (data && !data.storeListingFound) {
    return (
      <EmptyState
        title="Publish this app to view ratings and reviews"
        description="Ratings and user reviews are available after the app is live in the Solana dApp Store."
      />
    );
  }

  if (!data) {
    return <EmptyState title="No review data available" />;
  }

  if (data.reviews.length === 0) {
    if (hasActiveFilters && unfilteredCount > 0) {
      return <EmptyState title="No reviews match your filters" />;
    }
    return <EmptyState title="No reviews yet" />;
  }

  return (
    <div className="divide-y divide-seeker-800">
      {data.reviews.map((review, index) => (
        <ReviewItem
          key={review.id}
          index={index}
          review={review}
          canReply={data.canReply}
          isMutating={mutatingReviewId === review.id}
          onSaveReply={onSaveReply}
          onDeleteReply={onDeleteReply}
        />
      ))}
    </div>
  );
}

function ReviewItem({
  index,
  review,
  canReply,
  isMutating,
  onSaveReply,
  onDeleteReply,
}: {
  index: number;
  review: ReviewRow;
  canReply: boolean;
  isMutating: boolean;
  onSaveReply: (review: ReviewRow, reply: string) => Promise<boolean>;
  onDeleteReply: (review: ReviewRow) => Promise<void>;
}) {
  const reviewerLabel = getReviewerLabel(review);
  const [isComposing, setIsComposing] = useState(false);
  const [replyDraft, setReplyDraft] = useState(
    review.publisherReply?.review ?? '',
  );
  const replyTextareaId = `publisher-reply-${review.id}`;
  const canWriteReply = canReply && (review.review?.trim().length ?? 0) > 0;
  const hasReply = Boolean(review.publisherReply);
  const isReplyTooLong = replyDraft.trim().length > REPLY_MAX_LENGTH;
  const isReplyEmpty = replyDraft.trim().length === 0;

  useEffect(() => {
    if (!isComposing) {
      setReplyDraft(review.publisherReply?.review ?? '');
    }
  }, [isComposing, review.publisherReply?.review]);

  const handleSubmit = () => {
    void onSaveReply(review, replyDraft).then((saved) => {
      if (saved) {
        setIsComposing(false);
      }
    });
  };

  return (
    // Keyed on review id upstream, so the stagger only plays on first mount.
    <motion.article
      {...listItem(index)}
      className="flex flex-col gap-3 py-6 last:pb-0"
    >
      <div className="flex items-center gap-2">
        <InitialsAvatar name={reviewerLabel} size={32} />
        <div className="min-w-0">
          <div
            className="truncate text-sm font-bold text-white"
            title={reviewerLabel}
          >
            {reviewerLabel}
          </div>
          <ReviewerSubtitle review={review} />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <StarRating rating={review.rating} size={14} />
        <span className="text-xs leading-4 tabular-nums text-seeker-400">
          {formatReviewDate(review.createdAt)}
        </span>
      </div>

      <p
        className={cn(
          'text-sm leading-5',
          review.review ? 'text-white' : 'text-seeker-500',
        )}
      >
        {review.review ?? 'No written review.'}
      </p>

      {/* One flex slot for reply card / Reply button / composer; `empty:hidden`
          drops the slot (and its gap) for reviews that can't be replied to. */}
      <div className="empty:hidden">
        <AnimatePresence initial={false}>
          {canWriteReply && hasReply && !isComposing ? (
            <Collapse key="reply-card">
              <PublisherReplyCard
                reply={review.publisherReply}
                isMutating={isMutating}
                onEdit={() => setIsComposing(true)}
                onDelete={() => void onDeleteReply(review)}
              />
            </Collapse>
          ) : null}

          {canWriteReply && !hasReply && !isComposing ? (
            <Collapse key="reply-button">
              <Button
                variant="outlineWhite"
                onClick={() => setIsComposing(true)}
                disabled={isMutating}
                className="h-9 rounded-full"
              >
                <Reply className="size-4" />
                Reply
              </Button>
            </Collapse>
          ) : null}

          {canWriteReply && isComposing ? (
            <Collapse key="composer">
              <ReplyComposer
                id={replyTextareaId}
                replyDraft={replyDraft}
                isMutating={isMutating}
                isReplyEmpty={isReplyEmpty}
                isReplyTooLong={isReplyTooLong}
                onChange={setReplyDraft}
                onCancel={() => setIsComposing(false)}
                onSubmit={handleSubmit}
              />
            </Collapse>
          ) : null}
        </AnimatePresence>
      </div>
    </motion.article>
  );
}

/** Height + fade expand (180ms) / collapse (120ms) for the reply controls. */
function Collapse({ children }: { children: ReactNode }) {
  return (
    <motion.div
      initial={{ height: 0, opacity: 0, overflow: 'hidden' }}
      animate={{
        height: 'auto',
        opacity: 1,
        transition: { duration: 0.18, ease: EASE_OUT },
        // Clip only while the height animates so focus rings aren't cut off after.
        transitionEnd: { overflow: 'visible' },
      }}
      exit={{
        height: 0,
        opacity: 0,
        overflow: 'hidden',
        transition: { duration: 0.12, ease: EASE_OUT },
      }}
    >
      {children}
    </motion.div>
  );
}

function ReviewerSubtitle({ review }: { review: ReviewRow }) {
  return (
    <div className="text-xs leading-4 text-seeker-400">
      {review.isReviewerAccountDeleted
        ? 'Deleted account'
        : review.domain
          ? `via ${review.domain}`
          : review.walletAddress
            ? truncateMiddle(review.walletAddress)
            : 'Anonymous'}
    </div>
  );
}

function PublisherReplyCard({
  reply,
  isMutating,
  onEdit,
  onDelete,
}: {
  reply: ReviewRow['publisherReply'];
  isMutating: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const publisherName = reply?.publisherName ?? 'Publisher';

  return (
    <div className="rounded-lg bg-seeker-800 p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <InitialsAvatar name={publisherName} size={32} />
          <div className="min-w-0">
            <div className="truncate text-sm font-bold text-white">
              Your reply
            </div>
            <div className="text-xs leading-4 text-seeker-400">
              {publisherName}
              {reply ? ` · ${formatReviewDate(reply.updatedAt)}` : ''}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            variant="ghostMuted"
            onClick={onEdit}
            disabled={isMutating}
            className="size-8 px-0"
            aria-label="Edit publisher reply"
          >
            <Pencil className="size-4" />
          </Button>
          <Button
            variant="ghostMuted"
            onClick={onDelete}
            disabled={isMutating}
            className="size-8 px-0 hover:text-rose-400"
            aria-label="Delete publisher reply"
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>
      <p className="mt-4 whitespace-pre-wrap rounded-lg border border-seeker-700 bg-seeker-900 p-4 text-sm leading-5 text-white sm:ml-10">
        {reply?.review}
      </p>
    </div>
  );
}

function ReplyComposer({
  id,
  replyDraft,
  isMutating,
  isReplyEmpty,
  isReplyTooLong,
  onChange,
  onCancel,
  onSubmit,
}: {
  id: string;
  replyDraft: string;
  isMutating: boolean;
  isReplyEmpty: boolean;
  isReplyTooLong: boolean;
  onChange: (next: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  const overLimit = replyDraft.trim().length - REPLY_MAX_LENGTH;
  const { activePublisher } = usePublisher();

  return (
    <div className="rounded-lg bg-seeker-800 p-6">
      <div className="flex gap-2">
        <InitialsAvatar name={activePublisher?.name ?? 'Publisher'} size={32} />
        <div className="min-w-0 flex-1">
          <label htmlFor={id} className="text-sm font-bold text-white">
            Your Reply
          </label>
          <p className="text-sm text-seeker-400">
            You may send one reply per review. If the user updates this review,
            you will be notified and may edit your reply.
          </p>
        </div>
      </div>
      <div className="mt-3 sm:ml-10">
        <textarea
          id={id}
          aria-label="Publisher response"
          placeholder="Write a public reply..."
          value={replyDraft}
          onChange={(event) => onChange(event.target.value)}
          disabled={isMutating}
          rows={4}
          autoFocus
          className={cn(
            'block w-full resize-none rounded-lg border bg-seeker-900 px-4 py-3 text-sm leading-5 text-white outline-none transition-colors duration-150 placeholder:text-seeker-500 focus:border-seeker-400',
            isReplyTooLong ? 'border-orange-400' : 'border-seeker-700',
          )}
        />
        {isReplyTooLong ? (
          <CharacterCountHint
            className="mt-2 text-left text-orange-400"
            text={`${overLimit} ${overLimit === 1 ? 'character' : 'characters'} over the limit`}
          />
        ) : (
          <CharacterCountHint
            className="mt-2 text-left"
            text={charactersLeft(replyDraft.trim(), REPLY_MAX_LENGTH)}
          />
        )}
        <div className="mt-4 flex items-center justify-end gap-2">
          <Button
            variant="ghostMuted"
            onClick={onCancel}
            disabled={isMutating}
            className="h-9"
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={isMutating || isReplyEmpty || isReplyTooLong}
            className="h-9 min-w-[180px] bg-seeker-green border-transparent text-white hover:bg-seeker-green/80 disabled:opacity-100 disabled:bg-seeker-700 disabled:text-seeker-800"
            aria-label="Send publisher reply"
          >
            Submit
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Mirrors three `ReviewItem` rows: 32px avatar + name/subtitle lines, the
 * 16px stars/date row and two 20px body lines, separated by the same hairlines.
 */
function ReviewListSkeleton() {
  return (
    <SkeletonGroup
      label="Loading reviews"
      className="divide-y divide-seeker-800"
    >
      {[0, 1, 2].map((index) => (
        <div key={index} className="flex flex-col gap-3 py-6 last:pb-0">
          <div className="flex items-center gap-2">
            <Skeleton className="size-8 shrink-0" rounded="full" />
            <div className="flex flex-col">
              <div className="flex h-5 items-center">
                <Skeleton className="h-3.5 w-36" />
              </div>
              <div className="flex h-4 items-center">
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
          </div>
          <div className="flex h-4 items-center gap-2">
            <Skeleton className="h-3.5 w-[74px]" />
            <Skeleton className="h-3 w-20" />
          </div>
          <SkeletonText lines={2} />
        </div>
      ))}
    </SkeletonGroup>
  );
}

function EmptyState({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  return (
    <div className="rounded-lg border border-seeker-800 bg-seeker-950/40 p-8 text-center">
      <div className="text-sm font-medium text-white">{title}</div>
      {description && (
        <div className="mx-auto mt-2 max-w-md text-xs leading-4 text-seeker-400">
          {description}
        </div>
      )}
    </div>
  );
}

function formatRating(value: number | null) {
  if (value === null || Number.isNaN(value)) {
    return '0.0';
  }

  return value.toFixed(1);
}

const reviewDateFormatter = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const fullDateFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
});

function formatReviewDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return reviewDateFormatter.format(date);
}

function formatFullDate(value: Date) {
  if (Number.isNaN(value.getTime())) {
    return null;
  }

  return fullDateFormatter.format(value);
}
